import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ArrowRight, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp,
  FileText, Loader2, LayoutGrid, RefreshCw, Search,
} from "lucide-react";
import { toLocalDisplay } from "../../utils/datetimeLocal";
import {
  useAdminGetExamQuery,
  useAdminGetDietsByExamQuery,
  useAdminGetExamAnalyticsQuery,
} from "../../api/examsApi";

/** Diets per page. */
const PER_PAGE = 10;

const DIET_STATUS: Record<string, string> = {
  open: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-gray-50 text-gray-500 border-gray-200",
  closed: "bg-rose-50 text-rose-700 border-rose-200",
  archived: "bg-amber-50 text-amber-700 border-amber-200",
};

type SortKey = "title" | "status" | "applications" | "opens" | "closes";
type SortDir = "asc" | "desc";

/** Clickable column header; shows which way the table is currently sorted. */
function SortHeader({
  label, col, sort, onSort, align = "left",
}: {
  label: string;
  col: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onSort: (k: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === col;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;

  return (
    <th className={`px-4 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 hover:text-gray-900 transition-colors ${
          active ? "text-gray-900" : ""
        }`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? "text-indigo-600" : "text-gray-300"}`} />
      </button>
    </th>
  );
}

const ts = (iso?: string | null) => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return isNaN(t) ? 0 : t;
};

/** The diets of one exam, each with how many applications it holds. */
export default function AdminExamApplicationsExamPage() {
  const { examId = "" } = useParams();
  const location = useLocation();
  const passed = (location.state ?? {}) as { examTitle?: string; examCode?: string };

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  // Newest application window first — the same order the API lists diets in.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: "opens", dir: "desc" });

  const { data: exam } = useAdminGetExamQuery(examId, { skip: !examId });
  const { data: diets = [], isLoading, isFetching, refetch } = useAdminGetDietsByExamQuery(examId, {
    skip: !examId,
  });
  const { data: analytics, refetch: refetchCounts } = useAdminGetExamAnalyticsQuery(
    { examId },
    { skip: !examId }
  );

  const title = (exam as any)?.title ?? passed.examTitle ?? "Exam";
  const code = (exam as any)?.code ?? passed.examCode ?? "";
  const total = (analytics as any)?.totals?.applications ?? 0;

  // by_diet: [{ exam_diet_id, c }] — applications per diet.
  const countByDiet = useMemo(() => {
    const m = new Map<string, number>();
    for (const row of ((analytics as any)?.by_diet ?? []) as any[]) {
      if (row?.exam_diet_id) m.set(String(row.exam_diet_id), Number(row.c ?? 0));
    }
    return m;
  }, [analytics]);

  const rows = useMemo(
    () =>
      (diets as any[]).map((d: any) => ({ ...d, applications: countByDiet.get(String(d.id)) ?? 0 })),
    [diets, countByDiet]
  );

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    if (!nq) return rows;
    return rows.filter((d: any) =>
      `${d.title ?? ""} ${d.status ?? ""}`.toLowerCase().includes(nq)
    );
  }, [rows, q]);

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a: any, b: any) => {
      switch (sort.key) {
        case "applications":
          return (a.applications - b.applications) * dir;
        case "opens":
          return (ts(a.applications_open_at) - ts(b.applications_open_at)) * dir;
        case "closes":
          return (ts(a.applications_close_at) - ts(b.applications_close_at)) * dir;
        case "status":
          return String(a.status ?? "").localeCompare(String(b.status ?? "")) * dir;
        default:
          return String(a.title ?? "").localeCompare(String(b.title ?? ""), undefined, {
            numeric: true,
            sensitivity: "base",
          }) * dir;
      }
    });
  }, [filtered, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(
    () => sorted.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE),
    [sorted, safePage]
  );

  useEffect(() => { setPage(1); }, [q, sort]);

  // Text sorts read best A→Z first; counts and dates read best largest first.
  const onSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "title" || key === "status" ? "asc" : "desc" }
    );

  const refreshAll = () => { refetch(); refetchCounts(); };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <Link
        to="/exams/admin/applications"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3"
      >
        <ChevronLeft className="w-4 h-4" /> All exams
      </Link>

      <div className="bg-white border rounded-xl p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                {code ? <span className="text-indigo-600">{code}</span> : null}
                {code ? " — " : ""}{title}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {total} application{total !== 1 ? "s" : ""} across {rows.length} diet
                {rows.length !== 1 ? "s" : ""} — choose a diet to work through.
              </p>
            </div>
          </div>
          <button
            onClick={refreshAll}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Search */}
        <div className="mt-5 relative max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search diets by title or status…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="mt-8 py-10 flex justify-center text-gray-300">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-6 py-16 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-center">
            <LayoutGrid className="w-8 h-8 text-gray-300 mb-3" />
            <h2 className="font-bold text-gray-800">No diets</h2>
            <p className="text-sm text-gray-500 mt-1">
              This exam has no diets yet, so it has no applications.
            </p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="mt-6 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
            No diets match "{q}".
          </div>
        ) : (
          <>
            <div className="mt-5 border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                    <SortHeader label="Diet" col="title" sort={sort} onSort={onSort} />
                    <SortHeader label="Status" col="status" sort={sort} onSort={onSort} />
                    <SortHeader label="Applications" col="applications" sort={sort} onSort={onSort} align="right" />
                    <SortHeader label="Opens" col="opens" sort={sort} onSort={onSort} />
                    <SortHeader label="Closes" col="closes" sort={sort} onSort={onSort} />
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paged.map((d: any) => (
                    <tr key={d.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <Link
                          to={`/exams/admin/applications/exams/${examId}/diets/${d.id}`}
                          state={{ examTitle: title, examCode: code, dietTitle: d.title }}
                          className="font-medium text-gray-900 hover:text-indigo-600 transition-colors"
                        >
                          {d.title}
                        </Link>
                        {d.is_active && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border bg-indigo-50 text-indigo-600 border-indigo-100">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${
                            DIET_STATUS[d.status] ?? "bg-gray-50 text-gray-500 border-gray-200"
                          }`}
                        >
                          {d.status ?? "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-bold border ${
                            d.applications > 0
                              ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                              : "bg-gray-50 text-gray-400 border-gray-200"
                          }`}
                        >
                          {d.applications}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {toLocalDisplay(d.applications_open_at)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {toLocalDisplay(d.applications_close_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <Link
                            to={`/exams/admin/applications/exams/${examId}/diets/${d.id}`}
                            state={{ examTitle: title, examCode: code, dietTitle: d.title }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50 transition-colors whitespace-nowrap"
                          >
                            View applications
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
              <p className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-700">{(safePage - 1) * PER_PAGE + 1}</span>–
                <span className="font-medium text-gray-700">
                  {Math.min(safePage * PER_PAGE, sorted.length)}
                </span>{" "}
                of <span className="font-medium text-gray-700">{sorted.length}</span> diet
                {sorted.length !== 1 ? "s" : ""}
              </p>

              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-1.5 rounded-lg border bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: pageCount }, (_, i) => i + 1)
                    .filter((n) => n === 1 || n === pageCount || Math.abs(n - safePage) <= 1)
                    .map((n, i, arr) => (
                      <React.Fragment key={n}>
                        {i > 0 && n - arr[i - 1] > 1 && <span className="px-1 text-xs text-gray-300">…</span>}
                        <button
                          onClick={() => setPage(n)}
                          className={`min-w-[2rem] px-2 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                            n === safePage
                              ? "bg-gray-900 text-white border-gray-900"
                              : "bg-white text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          {n}
                        </button>
                      </React.Fragment>
                    ))}

                  <button
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={safePage === pageCount}
                    className="p-1.5 rounded-lg border bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
